import { useId } from 'react';

import { getBrand } from '@/data/brands';
import { cn } from '@/lib/cn';
import type { PetType } from '@/types/catalog';

interface ProductPackProps {
  brandId: string;
  petType: PetType;
  /** Short label printed on the bag, e.g. "15 kg". */
  weightLabel?: string;
  /**
   * Nome do produto para o rótulo acessível. Sem ele o desenho é tratado como
   * decorativo — evita que um leitor de tela anuncie "Embalagem Golden" oito
   * vezes seguidas numa grade.
   */
  productName?: string;
  className?: string;
}

/**
 * Draws a ração bag in the brand's own colours.
 *
 * The client has no product photography yet, and mismatched stock photos read
 * as a broken catalogue. This renders a consistent, on-brand pack for every
 * product instead — zero network requests, no third-party artwork, and it
 * degrades gracefully the day real photos arrive (set `product.image` and this
 * component simply stops being used).
 */
export function ProductPack({
  brandId,
  petType,
  weightLabel,
  productName,
  className,
}: ProductPackProps) {
  const gradientId = useId();
  const brand = getBrand(brandId);

  const bg = brand?.bg ?? '#B77945';
  const fg = brand?.fg ?? '#FFFFFF';
  const isLightBag = bg.toUpperCase() === '#FFFFFF';

  // The label band is 100 units wide; shrink the type so long names still fit.
  const brandLabel = (brand?.name ?? 'BrucePets').toUpperCase();
  const brandFontSize = brandLabel.length > 11 ? 11 : brandLabel.length > 8 ? 13 : 16;

  return (
    <svg
      viewBox="0 0 200 240"
      className={cn('h-full w-full', className)}
      {...(productName
        ? { role: 'img', 'aria-label': `Ilustração da embalagem: ${productName}` }
        : { 'aria-hidden': true })}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.18" />
          <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0.02" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.12" />
        </linearGradient>
      </defs>

      {/* Ground shadow — grounds the bag so it does not float. */}
      <ellipse cx="100" cy="226" rx="58" ry="8" fill="#5A3218" opacity="0.14" />

      <g className="origin-bottom transition-transform duration-500 ease-[var(--ease-out-soft)] group-hover:-rotate-2">
        {/* Bag body */}
        <path
          d="M46 46h108a10 10 0 0 1 10 10v154a12 12 0 0 1-12 12H48a12 12 0 0 1-12-12V56a10 10 0 0 1 10-10Z"
          fill={bg}
          stroke={isLightBag ? '#E8CDB4' : 'none'}
          strokeWidth="2"
        />

        {/* Top seal, darkened by overlay so it works on any brand colour. */}
        <path
          d="M44 22h112a8 8 0 0 1 8 8v18H36V30a8 8 0 0 1 8-8Z"
          fill={bg}
          stroke={isLightBag ? '#E8CDB4' : 'none'}
          strokeWidth="2"
        />
        <path d="M44 22h112a8 8 0 0 1 8 8v18H36V30a8 8 0 0 1 8-8Z" fill="#000" opacity="0.16" />

        {/* Crimp stitching on the seal */}
        <g stroke={fg} strokeWidth="1.6" opacity="0.35" strokeLinecap="round">
          <line x1="52" y1="34" x2="52" y2="40" />
          <line x1="68" y1="34" x2="68" y2="40" />
          <line x1="84" y1="34" x2="84" y2="40" />
          <line x1="100" y1="34" x2="100" y2="40" />
          <line x1="116" y1="34" x2="116" y2="40" />
          <line x1="132" y1="34" x2="132" y2="40" />
          <line x1="148" y1="34" x2="148" y2="40" />
        </g>

        {/* Lighting pass over the whole bag */}
        <path
          d="M46 46h108a10 10 0 0 1 10 10v154a12 12 0 0 1-12 12H48a12 12 0 0 1-12-12V56a10 10 0 0 1 10-10Z"
          fill={`url(#${gradientId})`}
        />

        {/* Brand label band */}
        <rect x="50" y="68" width="100" height="34" rx="9" fill={fg} opacity="0.94" />
        <text
          x="100"
          y="86"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={brandFontSize}
          fontWeight="800"
          fill={bg}
          fontFamily="Poppins, sans-serif"
          letterSpacing="-0.2"
        >
          {brandLabel}
        </text>

        {/* Copy lines standing in for the pack's small print */}
        <rect x="60" y="110" width="80" height="5" rx="2.5" fill={fg} opacity="0.42" />
        <rect x="70" y="122" width="60" height="5" rx="2.5" fill={fg} opacity="0.28" />

        {/* Pet mark in the lower third of the bag */}
        <g fill={fg} opacity="0.9" transform="translate(100 168)">
          {petType === 'dog' ? (
            <>
              {/* floppy-eared dog head — ears tilt out so they read separately */}
              <ellipse cx="-25" cy="4" rx="8" ry="18" transform="rotate(-16 -25 4)" />
              <ellipse cx="25" cy="4" rx="8" ry="18" transform="rotate(16 25 4)" />
              <ellipse cx="0" cy="3" rx="23" ry="22" />
              <ellipse cx="-8.5" cy="-3" rx="3.4" ry="4.4" fill={bg} />
              <ellipse cx="8.5" cy="-3" rx="3.4" ry="4.4" fill={bg} />
              <ellipse cx="0" cy="9" rx="8" ry="6" fill={bg} opacity="0.9" />
              <ellipse cx="0" cy="7" rx="3.2" ry="2.4" />
            </>
          ) : (
            <>
              {/* pointy-eared cat head */}
              <path d="M-24-8-28-30l17 9Z" />
              <path d="M24-8 28-30l-17 9Z" />
              <ellipse cx="0" cy="2" rx="25" ry="22" />
              <ellipse cx="-9" cy="-3" rx="3.4" ry="4.6" fill={bg} />
              <ellipse cx="9" cy="-3" rx="3.4" ry="4.6" fill={bg} />
              <path d="M0 6l-4 4h8Z" fill={bg} />
              <g stroke={bg} strokeWidth="1.6" strokeLinecap="round">
                <line x1="-11" y1="10" x2="-26" y2="7" />
                <line x1="-11" y1="13" x2="-25" y2="15" />
                <line x1="11" y1="10" x2="26" y2="7" />
                <line x1="11" y1="13" x2="25" y2="15" />
              </g>
            </>
          )}
        </g>

        {/* Weight badge, bottom-right of the bag */}
        {weightLabel && (
          <>
            <rect x="96" y="196" width="60" height="20" rx="10" fill={fg} opacity="0.9" />
            <text
              x="126"
              y="210"
              textAnchor="middle"
              fontSize="11"
              fontWeight="700"
              fill={bg}
              fontFamily="Poppins, sans-serif"
            >
              {weightLabel}
            </text>
          </>
        )}
      </g>

      {/* Spilled kibble at the base */}
      <g fill="#8A5A2E" opacity="0.55">
        <ellipse cx="34" cy="219" rx="5" ry="3.6" />
        <ellipse cx="24" cy="224" rx="4.2" ry="3.1" />
        <ellipse cx="170" cy="221" rx="4.6" ry="3.4" />
        <ellipse cx="179" cy="226" rx="3.8" ry="2.9" />
      </g>
    </svg>
  );
}
